/**
 * Story 052 — Address section.
 *
 * Zitadel uses singular `address` (replace-semantics). Save button PUTs
 * all six fields (region included even if blank — AC6 enforcement).
 *
 * SB6-AUTH-ADDR-01 — the Country `<TextField>` was the source of the
 * recurring `PUT /me/address` 400: BE expects ISO 3166-1 alpha-2 and
 * the free-text input let users type "Austria", "DE", "Aus", etc. The
 * Country slot is now an `<Autocomplete>` typeahead fed by
 * `useCountries()` (cached forever); it stores `alpha2` and renders
 * options as `{alpha2} - {fullName}` per Christian's OQ-SB6-A3 wording.
 */
import { useEffect, useMemo, useState, type ReactElement } from "react";
import {
  Autocomplete,
  Button,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { SectionShell } from "./SectionShell.js";
import {
  useCountries,
  useUpdateAddress,
  type AddressFields,
  type CountryEntry,
  type MeExpanded,
} from "../queries/index.js";
import { useToast } from "../ToastContext.js";

interface Props {
  me: MeExpanded;
}

const EMPTY_ADDRESS: AddressFields = {
  streetAddress: null,
  locality: null,
  postalCode: null,
  region: null,
  country: null,
  formatted: null,
};

export function AddressSection({ me }: Props): ReactElement {
  const update = useUpdateAddress();
  const { toast } = useToast();
  const initial = me.address ?? EMPTY_ADDRESS;
  const [streetAddress, setStreetAddress] = useState(
    initial.streetAddress ?? "",
  );
  const [locality, setLocality] = useState(initial.locality ?? "");
  const [postalCode, setPostalCode] = useState(initial.postalCode ?? "");
  const [region, setRegion] = useState(initial.region ?? "");
  const [country, setCountry] = useState(initial.country ?? "");

  useEffect(() => {
    setStreetAddress(initial.streetAddress ?? "");
    setLocality(initial.locality ?? "");
    setPostalCode(initial.postalCode ?? "");
    setRegion(initial.region ?? "");
    setCountry(initial.country ?? "");
  }, [
    initial.streetAddress,
    initial.locality,
    initial.postalCode,
    initial.region,
    initial.country,
  ]);

  function save() {
    const body: AddressFields = {
      streetAddress: streetAddress || null,
      locality: locality || null,
      postalCode: postalCode || null,
      region: region || null,
      country: country || null,
      formatted: initial.formatted,
    };
    update.mutate(body, {
      onSuccess: () => {
        toast("Address saved");
      },
      onError: () => {
        toast("Couldn't save address", "error");
      },
    });
  }

  return (
    <SectionShell id="address" title="Address">
      <Stack spacing={2}>
        <Typography component="p" variant="body2" color="text.secondary">
          {[
            initial.streetAddress,
            initial.postalCode,
            initial.locality,
            initial.region,
            initial.country,
          ]
            .filter((s): s is string => Boolean(s))
            .join(" · ") || "No address on file."}
        </Typography>
        <TextField
          label="Street address"
          value={streetAddress}
          onChange={(e) => {
            setStreetAddress(e.target.value);
          }}
          fullWidth
        />
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
          <TextField
            label="City"
            value={locality}
            onChange={(e) => {
              setLocality(e.target.value);
            }}
            fullWidth
          />
          <TextField
            label="Postal code"
            value={postalCode}
            onChange={(e) => {
              setPostalCode(e.target.value);
            }}
            fullWidth
          />
        </Stack>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
          <TextField
            label="Region"
            value={region}
            onChange={(e) => {
              setRegion(e.target.value);
            }}
            fullWidth
          />
          <CountryAutocomplete
            value={country}
            onChange={(next) => {
              setCountry(next);
            }}
          />
        </Stack>
        <Button
          variant="contained"
          sx={{ alignSelf: "flex-start" }}
          onClick={save}
          disabled={update.isPending}
        >
          Save address
        </Button>
      </Stack>
    </SectionShell>
  );
}

interface CountryAutocompleteProps {
  value: string;
  onChange: (alpha2: string) => void;
}

function CountryAutocomplete({
  value,
  onChange,
}: CountryAutocompleteProps): ReactElement {
  const { data: countries, isPending } = useCountries();
  const list = useMemo<readonly CountryEntry[]>(
    () => countries ?? [],
    [countries],
  );
  const byAlpha2 = useMemo(() => {
    const m = new Map<string, CountryEntry>();
    for (const c of list) m.set(c.alpha2, c);
    return m;
  }, [list]);
  const selectedOption = value
    ? (byAlpha2.get(value.toUpperCase()) ?? null)
    : null;
  return (
    <Autocomplete<CountryEntry>
      fullWidth
      loading={isPending}
      options={list}
      value={selectedOption}
      onChange={(_event, next) => {
        onChange(next?.alpha2 ?? "");
      }}
      getOptionLabel={(opt) => `${opt.alpha2} - ${opt.nameEn}`}
      isOptionEqualToValue={(opt, val) => opt.alpha2 === val.alpha2}
      filterOptions={(opts, state) => {
        const needle = state.inputValue.trim().toLowerCase();
        if (needle.length === 0) return opts;
        return opts.filter(
          (c) =>
            c.alpha2.toLowerCase().includes(needle) ||
            c.alpha3.toLowerCase().includes(needle) ||
            c.nameEn.toLowerCase().includes(needle) ||
            c.nameDe.toLowerCase().includes(needle),
        );
      }}
      renderOption={(props, opt) => (
        <li {...props} key={opt.alpha2}>
          {opt.alpha2} - {opt.nameEn}
        </li>
      )}
      renderInput={(params) => {
        // exactOptionalPropertyTypes is incompatible with MUI's
        // Autocomplete<→>TextField param-spread idiom (`size`,
        // `InputLabelProps.className/style`, etc. are all typed
        // `T | undefined`). Cast to the structurally-compatible
        // unknown-as-any boundary just for the spread; the prop
        // shapes are runtime-equivalent.
        // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-assignment
        const p = params as any;
        return (
          <TextField {...p} label="Country" placeholder="Type to search" />
        );
      }}
    />
  );
}
